import {
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { getUnlinkedSaiaLicenses, linkLicenseToAccount, unlinkLicenseFromAccount } from 'API/linkedin-accounts';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import type { License, LinkedLicenseSummary } from 'types/licenses.types';

const PRODUCT_LABELS: Record<string, string> = {
  saia_recruiting: 'SAIA Recruiting',
  saia_sales: 'SAIA Sales',
};

interface Props {
  open: boolean;
  accountId: string;
  linkedLicenses: LinkedLicenseSummary[];
  onClose(): void;
  onSaved(): void;
}

const AssignLicenseDialog: React.FC<Props> = ({ open, accountId, linkedLicenses, onClose, onSaved }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [unlinkedLicenses, setUnlinkedLicenses] = useState<License[]>([]);
  const [selectedLicenseId, setSelectedLicenseId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  /**
   * The backend rejects a link for four distinct, legitimate reasons (wrong product,
   * inactive licence, licence already linked, account already holds one of that product).
   * All four used to land in a branch that did nothing at all: no message, no toast, the
   * dialog simply stayed open. Reported as "Lizenz zuweisen funktioniert nicht" — from the
   * user's side an unexplained refusal is indistinguishable from a broken button.
   */
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (open) {
      setSelectedLicenseId(null);
      setError('');
      (async () => {
        const res = await getUnlinkedSaiaLicenses();
        if (Array.isArray(res)) {
          setUnlinkedLicenses(res);
        }
      })();
    }
  }, [open]);

  const readError = (res: any): string =>
    res?.error?.message || res?.error?.error || t('assignLicenseFailed');

  const handleAssign = async () => {
    if (!selectedLicenseId) return;
    setLoading(true);
    setError('');
    try {
      const res = await linkLicenseToAccount(selectedLicenseId, accountId);
      if (!res.error) {
        onSaved();
        onClose();
      } else {
        setError(readError(res));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async (licenseId: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await unlinkLicenseFromAccount(licenseId);
      if (!res.error) {
        onSaved();
        onClose();
      } else {
        setError(readError(res));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth classes={{ paper: classes.paper }}>
      <DialogTitle className={classes.title}>{t('assignLicenseTitle')}</DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {linkedLicenses.length > 0 && (
            <>
              <Typography className={classes.sectionTitle}>{t('license')}</Typography>
              {linkedLicenses.map((lic) => (
                <Stack key={lic._id} direction="row" alignItems="center" justifyContent="space-between">
                  <Chip
                    label={PRODUCT_LABELS[lic.product] || lic.product}
                    size="small"
                    color={lic.isActive && !lic.isExpired ? 'success' : 'error'}
                    variant="outlined"
                  />
                  <PaiLink
                    text={t('unassignLicense')}
                    size="sm"
                    underline="always"
                    action={() => handleUnassign(lic._id)}
                  />
                </Stack>
              ))}
            </>
          )}

          <Typography className={classes.sectionTitle}>{t('assignLicenseDescription')}</Typography>

          {unlinkedLicenses.length > 0 ? (
            <List dense disablePadding>
              {unlinkedLicenses.map((lic) => (
                <ListItemButton
                  key={lic._id}
                  selected={selectedLicenseId === lic._id}
                  onClick={() => setSelectedLicenseId(lic._id)}
                  className={classes.listItem}
                >
                  <ListItemText
                    primary={PRODUCT_LABELS[lic.product] || lic.product}
                    secondary={`${lic.billingType} ${lic.expiresAt ? `- expires ${new Date(lic.expiresAt).toLocaleDateString()}` : ''}`}
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('noUnlinkedLicenses')}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size="sm" underline="always" action={onClose} />
        <PaiButton
          text={t('assignLicense')}
          size="small"
          border="rounded"
          action={handleAssign}
          loading={loading}
          disabled={!selectedLicenseId}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: { borderRadius: '12px' },
  title: { fontSize: theme.typography.pxToRem(16), fontWeight: 'bold' },
  content: { padding: '16px 24px' },
  sectionTitle: { fontSize: theme.typography.pxToRem(14), fontWeight: 600 },
  actions: { padding: '8px 24px 16px 24px' },
  listItem: {
    borderRadius: '8px',
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.light + '22',
    },
  },
}));

export default AssignLicenseDialog;
